import { computed, type Ref } from "vue";
import maxSize from "popper-max-size-modifier";
import type { Modifier, ModifierArguments } from "@popperjs/core";
import { getReactiveMediaQuery } from "./vue";

const breakpointMinWidth = {
	// See https://getbootstrap.com/docs/5.3/layout/breakpoints/#available-breakpoints
	xs: 0,
	sm: 576,
	md: 768,
	lg: 992,
	xl: 1200,
	xxl: 1400
};

export type Breakpoint = keyof typeof breakpointMinWidth;

export const breakpoints = Object.keys(breakpointMinWidth) as Breakpoint[];

const mediaQueries = Object.fromEntries(Object.entries(breakpointMinWidth).map(([breakpoint, minWidth]) => [breakpoint, getReactiveMediaQuery(`(min-width: ${minWidth}px)`)]));
export const reactiveBreakpoint = computed(() => [...breakpoints].reverse().find((breakpoint) => mediaQueries[breakpoint].value) ?? 'xs');

export function isMaxBreakpoint(breakpoint: Breakpoint): boolean {
	return breakpoints.indexOf(reactiveBreakpoint.value) <= breakpoints.indexOf(breakpoint);
}

export function isNarrowBreakpoint(): boolean {
	return isMaxBreakpoint("sm");
}

/**
 * Returns a reactive boolean that is true if the current breakpoint is the specified one or smaller.
 */
export function useMaxBreakpoint(breakpoint: Breakpoint): Ref<boolean> {
	return computed(() => isMaxBreakpoint(breakpoint));
}

export function useIsNarrow(): Ref<boolean> {
	return computed(() => isNarrowBreakpoint());
}

/**
 * Returns a reactive boolean that is true if the current breakpoint is the specified one or larger.
 */
export function useMinBreakpoint(breakpoint: Breakpoint): Ref<boolean> {
	return computed(() => breakpoints.indexOf(reactiveBreakpoint.value) >= breakpoints.indexOf(breakpoint));
}

export type ThemeColour = "primary" | "secondary" | "success" | "danger" | "warning" | "info" | "light" | "dark";
export type ButtonVariant = ThemeColour | "link" | "outline-primary" | "outline-secondary" | "outline-success" | "outline-danger" | "outline-warning" | "outline-info" | "outline-light" | "outline-dark";
export type ButtonSize = "lg" | "sm";

/**
 * An array of popper modifiers that uses popper-max-size-modifier to shrink the popover to prevent overflow
 * rather than move it, as is the default in Bootstrap.
 */
export const getMaxSizeModifiers = ({ maxWidth = "30rem" }: { maxWidth?: string } = {}): Array<Partial<Modifier<any, any>>> => [
	{
		...maxSize,
		options: {
			padding: 5
		}
	},
	{
		name: 'applyMaxSize',
		enabled: true,
		phase: 'beforeWrite',
		requires: ['maxSize'],
		fn({ state }: ModifierArguments<any>): void {
			// The `maxSize` modifier provides this data
			const {width, height} = state.modifiersData.maxSize;

			state.styles.popper = {
				...state.styles.popper,
				maxWidth: `min(${maxWidth}, ${width}px)`,
				maxHeight: `${height}px`
			}
		}
	}
];

/**
 * Returns true if any modal dialog is currently open.
 */
export function isModalOpen(): boolean {
	return !!document.querySelector(".modal");
}

/**
 * Returns true if any dropdown menu is currently open.
 */
export function isDropdownOpen(except?: HTMLElement): boolean {
	const openMenu = document.querySelector(".dropdown-menu.show");
	return !!openMenu && (!except || openMenu !== except);
}